import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import { isTokenValid } from "../authGard/auth";
import AccountTransaction from "../pages/AccountTransaction";
import AdminDashBoard from "../pages/AdminDashBoard";
import UserManagement from "../pages/userManagement";
import SubscriptionExpiredPage from "../pages/SubscriptionExpiredPage";
import SuspendPage from "../pages/SuspendPage";
import HomePage from "../components/HomePage";
import UserProfile from "../components/userProfile";
import RootRedirector from "../utils/RootRedirector";
import PrivacyPolicy from "../pages/PrivacyPolicyPage";
import TermsAndCondition from "../pages/TermsAndCondition";
import RefundAndCancalation from "../pages/RefundAndCancalation";

// Lazy-loaded components
const Login = lazy(() => import("../pages/Login"));
const Register = lazy(() => import("../pages/Register"));
const SubscriptionPlanPage = lazy(() => import("../pages/SubscriptionPlanPage"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword"));
const ResetPassword = lazy(() => import("../pages/ResetPassword"));
const AdminPlansPage = lazy(() => import("../pages/AdminPlansPage"));
const LibraryDashboard = lazy(() => import("../components/LibraryDashboard"));
const CustomerViewPage = lazy(() => import("../components/CustomerViewModal"));
const LibraryManagementPage = lazy(() => import("../components/LibraryManagementPage"));
const AddBookPage = lazy(() => import("../components/AddBookPage"));
const IssueBookPage = lazy(() => import("../components/IssueBookPage"));
const AddCustomer = lazy(() => import("../components/addCustomer"));
const LibraryHistoryPage = lazy(() => import("../components/LibraryHistoryPage"));
const StockPage = lazy(() => import("../components/StockPage"));
const CustomerBank = lazy(() => import("../components/customerBank"));


// This guard is for 'Active' users only
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ isAuthenticated: isAuthenticated, message: "Session expired" }} />;
  }

  if (user.role == 'admin') {
    return <Navigate to="/library-admin" replace />
  }

  if (user.status === 'Suspended') {
    return <Navigate to='/library-SuspendPage' replace />;
  }

  if (user.status === 'Inactive') {
    if (user.haveSubscription === true) {
      // EXPIRED user
      return <Navigate to='/library-SubscriptionExpiredPage' replace />;
    } else {
      // NEW user, never paid
      return <Navigate to={`/choose-plan/${user._id}`} replace />;
    }
  }

  // Only 'Active' users will reach this point
  return children;
};

// This guard is for 'Admin' users only
const AdminProtected = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ isAuthenticated: isAuthenticated, message: "session expired" }} />
  }
  if (user.role != 'admin') {
    return <Navigate to="/library-dashboard" replace />
  }
  return children;
}

// This guard is for 'unauthenticated' users only (login, register)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();

  if (isAuthenticated) {
    // If a logged-in user tries to go to /login, send them to their dashboard
    if (user.role == 'admin') {
      return <Navigate to={"/library-admin"} replace />
    } else {
      // Send them to the dashboard, ProtectedRoute will handle their status
      return <Navigate to={"/library-dashboard"} replace />
    }
  }

  //not authenticated go to children (login/register)
  return children;
}


// GUARD 1: For NEW users (status: Inactive, haveSubscription: false)
const NewUserRoute = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/library-admin" replace />;
  }

  if (user.status === 'Active') {
    return <Navigate to="/library-dashboard" replace />;
  }

  if (user.status === 'Suspended') {
    return <Navigate to="/library-SuspendPage" replace />;
  }

  if (user.status === 'Inactive' && user.haveSubscription === true) {
    // This is an EXPIRED user, send them to the expired page
    return <Navigate to="/library-SubscriptionExpiredPage" replace />;
  }

  // ONLY allow NEW users (Inactive, haveSubscription: false)
  return children;
};

// GUARD 2: For EXPIRED users (status: Inactive, haveSubscription: true)
const SubscriptionExpiredGuard = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/library-admin" replace />;
  }

  if (user.status === 'Active') {
    return <Navigate to="/library-dashboard" replace />;
  }

  if (user.status === 'Suspended') {
    return <Navigate to="/library-SuspendPage" replace />;
  }

  if (user.status === 'Inactive' && user.haveSubscription === false) {
    // This is a NEW user, send them to the plan page
    return <Navigate to={`/choose-plan/${user._id}`} replace />;
  }

  // ONLY allow EXPIRED users (Inactive, haveSubscription: true)
  return children;
};

// GUARD 3: For SUSPENDED users (status: Suspended)
const SuspendedGuard = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'admin') {
    return <Navigate to="/library-admin" replace />;
  }

  if (user.status === 'Active') {
    return <Navigate to="/library-dashboard" replace />;
  }

  if (user.status === 'Inactive') {
    // Check which kind of Inactive they are
    if (user.haveSubscription === true) {
      return <Navigate to="/library-SubscriptionExpiredPage" replace />;
    } else {
      return <Navigate to={`/choose-plan/${user._id}`} replace />;
    }
  }

  // ONLY allow SUSPENDED users
  return children;
};

// In Router.js

// NEW GUARD: For the /choose-plan page
const ChoosePlanGuard = ({ children }) => {
  const { isAuthenticated, user } = isTokenValid();

  if (!isAuthenticated) {
    // 1. User is NOT authenticated (Register flow)
    // This is correct, let them see the page.
    return children; // ALLOW
  }

  // 2. User IS authenticated (Login flow or trying to access)

  if (user.role === 'admin') {
    // Admin should not be here
    return <Navigate to="/library-admin" replace />;
  }

  if (user.status === 'Active') {
    // Active user should not see this page
    return <Navigate to="/library-dashboard" replace />;
  }

  if (user.status === 'Suspended') {
    // Suspended user should be on their own page
    return <Navigate to="/library-SuspendPage" replace />;
  }

  if (user.status === 'Inactive') {
    if (user.haveSubscription === true) {
      // This is an EXPIRED user, send them to the expired page
      return <Navigate to="/library-SubscriptionExpiredPage" replace />;
    } else {
      // This is a NEW, logged-in user.
      // This is correct, let them see the page.
      return children; // ALLOW
    }
  }

  // Fallback
  return children;
};

const Router = [
  {
    path: "/",
    children: [
      { path: "/", element: <RootRedirector /> },
      {
        path: "/login",
        element: (
          <PublicRoute>
            <Login />
          </PublicRoute>
        )
      },
      {
        path: "/register",
        element: (
          <PublicRoute>
            <Register />
          </PublicRoute>
        )
      },
      {
        // =============================================
        //  FIX 1: Use NewUserRoute here
        // =============================================
        path: "/choose-plan/:userId",
        element: (
          <ChoosePlanGuard>
            <SubscriptionPlanPage />
          </ChoosePlanGuard>
        )
      },
      {
        path: "/forgot-password",
        element: (
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        )
      },
      {
        path: "/reset-password/:token",
        element: (
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        )
      },

      // ... All your <ProtectedRoute> routes are correct
      {
        path: "/library-dashboard",
        element: (
          <ProtectedRoute>
            <LibraryDashboard />
          </ProtectedRoute>
        )
      },
      {
        path: "/user-profile",
        element: (
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        )
      },
      // ... (other protected routes)
      {
        path: "/library-dashboard/customer/:id",
        element: (
          <ProtectedRoute>
            <CustomerViewPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-management",
        element: (
          <ProtectedRoute>
            <LibraryManagementPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/add-book",
        element: (
          <ProtectedRoute>
            <AddBookPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/issue-book",
        element: (
          <ProtectedRoute>
            <IssueBookPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/add-customer",
        element: (
          <ProtectedRoute>
            <AddCustomer />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/history",
        element: (
          <ProtectedRoute>
            <LibraryHistoryPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/stock",
        element: (
          <ProtectedRoute>
            <StockPage />
          </ProtectedRoute>
        )
      },
      {
        path: "/library-dashboard/bank-account",
        element: (
          <ProtectedRoute>
            <CustomerBank />
          </ProtectedRoute>
        )
      },
    ],
  },
  //admin route
  {
    path: '/library-admin',
    element: (
      <AdminProtected>
        <AdminDashBoard />
      </AdminProtected>
    )
  },
  {
    path: '/library-admin-transaction',
    element: (
      <AdminProtected>
        <AccountTransaction />
      </AdminProtected>
    )
  },
  {
    path: '/library-admin-plans',
    element: (
      <AdminProtected>
        <AdminPlansPage />
      </AdminProtected>
    )
  },
  {
    path: '/library-admin-userManagement',
    element: (
      <AdminProtected >
        <UserManagement />
      </AdminProtected>
    )
  },

  // =============================================
  //  FIX 2 & 3: Your status pages are correct
  // =============================================
  {
    path: '/library-SubscriptionExpiredPage',
    element: (
      <SubscriptionExpiredGuard>
        <SubscriptionExpiredPage />
      </SubscriptionExpiredGuard>
    )
  },
  {
    path: '/library-SuspendPage',
    element: (
      <SuspendedGuard>
        <SuspendPage />
      </SuspendedGuard>
    )
  },

  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />
  },
  {
    path: '/terms-and-condition',
    element: <TermsAndCondition />
  },
  {
    path: '/refund-and-cancellation',
    element: <RefundAndCancalation />
  }
];

export default Router;